import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tags, Plus, Trash2, X, Send, Save, Copy, Power, Pencil, ShieldCheck, Link2, Hand } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead } from "@/components/kit";
import { BrandLogo } from "@/components/BrandLogo";

const BLANK = {
  channel: "", mechanism: "buttons", message_type: "normal", mode: "toggle",
  title: "Choose Your Roles", description: "Select your roles below.", color: "#0EA5E9",
  author_name: "", author_icon: "", author_url: "", footer_text: "", footer_icon: "",
  image_url: "", thumbnail_url: "", show_timestamp: false, fields: [], button_color: "grey",
  roles: [{ emoji: "🎮", role: "", label: "", description: "", order: 0 }],
  verified_role: "", unverified_role: "", require_link: true, status: "published",
};
const MODES = [
  { v: "toggle", label: "Toggle (add / remove)" },
  { v: "add", label: "Add only" },
  { v: "remove", label: "Remove only" },
  { v: "single", label: "Single choice (unique)" },
];
const BTN_COLORS = { blurple: "#5865F2", green: "#248046", red: "#da373c", grey: "#4e5058" };

export default function ReactionRoles() {
  const { guildId } = useGuild();
  const [panels, setPanels] = useState(null);
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [linked, setLinked] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/guilds/${guildId}/reaction-roles`).then((r) => setPanels(r.data)).catch(() => setPanels([]));
  useEffect(() => {
    load();
    api.get(`/guilds/${guildId}/discord/channels`).then((r) => { setChannels(r.data.channels || []); setLinked(r.data.linked); }).catch(() => {});
    api.get(`/guilds/${guildId}/discord/roles`).then((r) => setRoles(r.data.roles || [])).catch(() => {});
  }, [guildId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRole = (i, k, v) => setForm((f) => ({ ...f, roles: f.roles.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const addRole = () => setForm((f) => ({ ...f, roles: [...f.roles, { emoji: "", role: "", label: "", description: "", order: f.roles.length }] }));
  const rmRole = (i) => setForm((f) => ({ ...f, roles: f.roles.filter((_, idx) => idx !== i) }));
  const setField = (i, k, v) => setForm((f) => ({ ...f, fields: f.fields.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const addField = () => setForm((f) => ({ ...f, fields: [...f.fields, { name: "", value: "", inline: false }] }));
  const rmField = (i) => setForm((f) => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));

  const reset = () => { setForm(BLANK); setEditingId(null); };

  const save = async (status) => {
    if (form.message_type === "verify") {
      if (!form.verified_role) return toast.error("Pick the role granted on verify");
    } else if (form.message_type === "binding") {
      // no roles required for binding
    } else if (form.roles.every((r) => !r.role)) {
      return toast.error("Add at least one role");
    }
    if (!form.channel && status === "published") return toast.error("Pick a channel to post to");
    const payload = { ...form, status, mode: form.message_type === "verify" ? "verify" : form.mode };
    setBusy(true);
    try {
      const url = editingId ? `/guilds/${guildId}/reaction-roles/${editingId}` : `/guilds/${guildId}/reaction-roles`;
      const { data } = await (editingId ? api.put(url, payload) : api.post(url, payload));
      toast.success(status === "draft" ? "Draft saved" : (editingId ? "Panel updated" : "Panel published to Discord 🎉"));
      if (data?.hint) toast.warning(data.hint, { duration: 8000 });
      reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save panel"); }
    finally { setBusy(false); }
  };

  const edit = (p) => { setForm({ ...BLANK, ...p }); setEditingId(p.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const duplicate = async (id) => { await api.post(`/guilds/${guildId}/reaction-roles/${id}/duplicate`); toast.success("Duplicated as draft"); load(); };
  const toggleStatus = async (p) => {
    const next = p.status === "disabled" ? "published" : "disabled";
    const { data } = await api.post(`/guilds/${guildId}/reaction-roles/${p.id}/status`, { config: { status: next } });
    toast.success(next === "disabled" ? "Panel disabled" : "Panel enabled");
    if (data?.hint) toast.warning(data.hint, { duration: 8000 });
    load();
  };
  const del = async (id) => { await api.delete(`/guilds/${guildId}/reaction-roles/${id}`); toast.success("Panel deleted"); load(); };

  const isVerify = form.message_type === "verify";
  const isBinding = form.message_type === "binding";

  return (
    <div>
      <PageHead title="Reaction Roles" desc="Build Carl-bot style self-role panels — advanced embeds, buttons or reactions, verify & account binding." />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* ---------- Builder ---------- */}
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-head font-semibold flex items-center gap-2">
                {editingId ? <Pencil className="w-4 h-4 text-volt" /> : <Plus className="w-4 h-4 text-volt" />}
                {editingId ? "Edit panel" : "New panel"}
              </h3>
              {editingId && <button data-testid="rr-cancel-edit" onClick={reset} className="text-xs text-zinc-400 hover:text-white">Cancel edit</button>}
            </div>

            {/* Type / mechanism / mode */}
            <div className="grid grid-cols-3 gap-3">
              <Field label="Message type">
                <Select testid="rr-type" value={form.message_type} onChange={(v) => set("message_type", v)}
                  options={[["normal", "Normal"], ["verify", "Verify"], ["binding", "Binding"]]} />
              </Field>
              <Field label="Mechanism">
                <Select testid="rr-mechanism" value={form.mechanism} onChange={(v) => set("mechanism", v)}
                  options={[["buttons", "Buttons"], ["reactions", "Reactions"], ["both", "Both"]]} />
              </Field>
              <Field label="Behaviour">
                <Select testid="rr-mode" value={isVerify ? "verify" : form.mode} disabled={isVerify} onChange={(v) => set("mode", v)}
                  options={isVerify ? [["verify", "Verify (one-time)"]] : MODES.map((m) => [m.v, m.label])} />
              </Field>
            </div>

            {/* Channel */}
            <Field label="Post to channel">
              <input data-testid="rr-channel" list="rr-channels" value={form.channel}
                onChange={(e) => set("channel", e.target.value)} placeholder={linked ? "Select a channel…" : "roles"}
                className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
              <datalist id="rr-channels">{channels.map((c) => <option key={c.id} value={c.name}>#{c.name}</option>)}</datalist>
              {!linked && <p className="text-xs text-zinc-600 mt-1">Server not linked to the live bot here — type the channel name; on your server you'll get a dropdown.</p>}
            </Field>

            {/* Embed content */}
            <div className="border-t border-zinc-800 pt-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Embed</div>
              <div className="grid grid-cols-[1fr,auto] gap-3">
                <Field label="Title"><input data-testid="rr-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inp} /></Field>
                <Field label="Colour"><input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="h-9 w-16 bg-black border border-zinc-800 rounded-md cursor-pointer" /></Field>
              </div>
              <Field label="Description"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inp + " text-sm"} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Author name"><input value={form.author_name} onChange={(e) => set("author_name", e.target.value)} className={inp} /></Field>
                <Field label="Author icon URL"><input value={form.author_icon} onChange={(e) => set("author_icon", e.target.value)} className={inp} /></Field>
                <Field label="Image URL"><input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…/banner.png" className={inp} /></Field>
                <Field label="Thumbnail URL"><input value={form.thumbnail_url} onChange={(e) => set("thumbnail_url", e.target.value)} className={inp} /></Field>
                <Field label="Footer text"><input value={form.footer_text} onChange={(e) => set("footer_text", e.target.value)} className={inp} /></Field>
                <Field label="Footer icon URL"><input value={form.footer_icon} onChange={(e) => set("footer_icon", e.target.value)} className={inp} /></Field>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={form.show_timestamp} onChange={(e) => set("show_timestamp", e.target.checked)} /> Timestamp</label>
                {form.mechanism !== "reactions" && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-zinc-500">Button colour</span>
                    <Select testid="rr-btncolor" value={form.button_color} onChange={(v) => set("button_color", v)} small
                      options={[["grey", "Grey"], ["blurple", "Blurple"], ["green", "Green"], ["red", "Red"]]} />
                  </div>
                )}
              </div>

              {/* Fields */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-zinc-500">Embed fields</span>
                  <button data-testid="rr-addfield" onClick={addField} className="text-xs text-volt hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add field</button>
                </div>
                {form.fields.map((f, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <input value={f.name} onChange={(e) => setField(i, "name", e.target.value)} placeholder="Field name" className="w-1/3 bg-black border border-zinc-800 rounded-md px-2 py-1.5 text-sm outline-none focus:border-volt" />
                    <input value={f.value} onChange={(e) => setField(i, "value", e.target.value)} placeholder="Field value" className="flex-1 bg-black border border-zinc-800 rounded-md px-2 py-1.5 text-sm outline-none focus:border-volt" />
                    <label className="text-xs text-zinc-400 flex items-center gap-1 pt-1.5"><input type="checkbox" checked={f.inline} onChange={(e) => setField(i, "inline", e.target.checked)} /> inline</label>
                    <button onClick={() => rmField(i)} className="text-zinc-500 hover:text-red-400 pt-1.5"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Verify config */}
            {isVerify && (
              <div className="border-t border-zinc-800 pt-4 grid grid-cols-2 gap-3">
                <Field label="Verified role (granted)">
                  <input data-testid="rr-verified-role" list="rr-roles" value={form.verified_role} onChange={(e) => set("verified_role", e.target.value)} placeholder="Verified" className={inp} />
                </Field>
                <Field label="Unverified role (removed, optional)">
                  <input list="rr-roles" value={form.unverified_role} onChange={(e) => set("unverified_role", e.target.value)} placeholder="Unverified" className={inp} />
                </Field>
                <Field label="Button / prompt label">
                  <input value={form.roles[0]?.label || ""} onChange={(e) => setRole(0, "label", e.target.value)} placeholder="✅ Verify" className={inp} />
                </Field>
                <Field label="Button emoji">
                  <input value={form.roles[0]?.emoji || ""} onChange={(e) => setRole(0, "emoji", e.target.value)} placeholder="✅" className={inp} />
                </Field>
              </div>
            )}

            {isBinding && (
              <div className="border-t border-zinc-800 pt-4 space-y-3">
                <div className="bg-black/30 rounded-lg p-3 text-xs text-zinc-400 flex gap-2">
                  <Link2 className="w-4 h-4 text-[#f59e0b] shrink-0 mt-0.5" />
                  {form.require_link
                    ? "Posts a \"Link your Discord to your crank.live account\" panel. When a member clicks, the bot checks if their Discord is signed in to crank.live and grants the linked-account role below."
                    : "Account link is OFF — clicking the button just grants the role below, no crank.live sign-in needed."}
                </div>
                <label className="flex items-center gap-2 text-sm text-zinc-300">
                  <input data-testid="rr-require-link" type="checkbox" checked={form.require_link} onChange={(e) => set("require_link", e.target.checked)} />
                  Require crank.live account link before granting the role
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Linked-account role (granted, optional)">
                    <input data-testid="rr-linked-role" list="rr-roles" value={form.verified_role} onChange={(e) => set("verified_role", e.target.value)} placeholder="Verified Member" className={inp} />
                  </Field>
                  <Field label="Button label">
                    <input value={form.roles[0]?.label || ""} onChange={(e) => setRole(0, "label", e.target.value)} placeholder={form.require_link ? "🔗 Link Account" : "🎉 Get Role"} className={inp} />
                  </Field>
                </div>
              </div>
            )}

            {/* Roles */}
            {!isVerify && (
              <div className="border-t border-zinc-800 pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-zinc-500">Role options</span>
                  <button data-testid="rr-addrole" onClick={addRole} className="text-xs text-volt hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add role</button>
                </div>
                {form.roles.map((r, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input value={r.emoji} onChange={(e) => setRole(i, "emoji", e.target.value)} placeholder="🎮" className="w-12 text-center bg-black border border-zinc-800 rounded-md px-1 py-2 outline-none focus:border-volt" />
                    <input data-testid={`rr-role-${i}`} list="rr-roles" value={r.role} onChange={(e) => setRole(i, "role", e.target.value)} placeholder="Role name" className="flex-1 bg-black border border-zinc-800 rounded-md px-2 py-2 outline-none focus:border-volt" />
                    <input value={r.label} onChange={(e) => setRole(i, "label", e.target.value)} placeholder="Label" className="w-28 bg-black border border-zinc-800 rounded-md px-2 py-2 outline-none focus:border-volt" />
                    {form.roles.length > 1 && <button onClick={() => rmRole(i)} className="text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>}
                  </div>
                ))}
                <datalist id="rr-roles">{roles.map((r) => <option key={r.id} value={r.name}>{r.assignable ? "" : "⚠ above bot"}</option>)}</datalist>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button data-testid="rr-save-draft" onClick={() => save("draft")} disabled={busy} className="flex-1 border border-zinc-700 text-zinc-200 rounded-md py-2 hover:bg-zinc-800 disabled:opacity-60 flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save draft</button>
              <button data-testid="rr-publish" onClick={() => save("published")} disabled={busy} className="flex-1 bg-volt text-volt-ink font-semibold rounded-md py-2 hover:bg-volt-hover disabled:opacity-60 flex items-center justify-center gap-2"><Send className="w-4 h-4" /> {busy ? "…" : (editingId ? "Update & publish" : "Publish")}</button>
            </div>
          </div>
        </div>

        {/* ---------- Preview + Management ---------- */}
        <div className="space-y-6">
          <div>
            <h3 className="font-head font-semibold mb-3 text-sm text-zinc-400">Live preview</h3>
            <DiscordPreview form={form} isVerify={isVerify} isBinding={isBinding} />
          </div>

          <div>
            <h3 className="font-head font-semibold mb-3">Panels</h3>
            {panels === null ? <p className="text-zinc-500">Loading…</p> : panels.length === 0 ? (
              <div className="text-center py-12 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl">
                <Tags className="w-10 h-10 mx-auto mb-3 text-zinc-700" /> No panels yet.
              </div>
            ) : (
              <div className="space-y-3" data-testid="rr-panels">
                {panels.map((p) => (
                  <div key={p.id} data-testid={`rr-panel-${p.id}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate flex items-center gap-2">
                          {p.message_type === "verify" && <ShieldCheck className="w-4 h-4 text-emerald-400" />}
                          {p.message_type === "binding" && <Link2 className="w-4 h-4 text-cyan-400" />}
                          {p.title}
                        </div>
                        <div className="text-xs text-zinc-500 flex flex-wrap gap-x-3 mt-0.5">
                          <span>#{p.channel}</span><span className="capitalize">{p.mechanism}</span><span className="capitalize">{p.mode}</span>
                          {p.message_id && <span>msg {String(p.message_id).slice(-6)}</span>}
                          <span>{p.selections || 0} uses</span>
                        </div>
                      </div>
                      <StatusPill status={p.status} />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {(p.roles || []).map((r, i) => <span key={i} className="text-xs bg-black border border-zinc-800 rounded-full px-2.5 py-1">{r.emoji} {r.label || r.role}</span>)}
                      {p.message_type === "verify" && <span className="text-xs bg-black border border-emerald-800 rounded-full px-2.5 py-1">✅ {p.verified_role}</span>}
                    </div>
                    <div className="flex gap-3 mt-3 text-xs">
                      <button data-testid={`rr-edit-${p.id}`} onClick={() => edit(p)} className="text-zinc-300 hover:text-volt flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                      <button onClick={() => duplicate(p.id)} className="text-zinc-300 hover:text-volt flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Duplicate</button>
                      <button data-testid={`rr-toggle-${p.id}`} onClick={() => toggleStatus(p)} className="text-zinc-300 hover:text-yellow-400 flex items-center gap-1"><Power className="w-3.5 h-3.5" /> {p.status === "disabled" ? "Enable" : "Disable"}</button>
                      <button data-testid={`rr-delete-${p.id}`} onClick={() => del(p.id)} className="text-zinc-300 hover:text-red-400 flex items-center gap-1 ml-auto"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt";

function Field({ label, children }) {
  return <div><label className="text-xs uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>{children}</div>;
}
function Select({ value, onChange, options, testid, disabled, small }) {
  return (
    <select data-testid={testid} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      className={`bg-black border border-zinc-800 rounded-md outline-none focus:border-volt disabled:opacity-60 ${small ? "px-2 py-1 text-sm" : "w-full px-3 py-2"}`}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
function StatusPill({ status }) {
  const map = { published: ["Published", "text-emerald-400 border-emerald-800 bg-emerald-500/10"],
                draft: ["Draft", "text-zinc-400 border-zinc-700 bg-zinc-800"],
                disabled: ["Disabled", "text-yellow-400 border-yellow-800 bg-yellow-500/10"] };
  const [t, c] = map[status] || map.draft;
  return <span data-testid="rr-status" className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border shrink-0 ${c}`}>{t}</span>;
}

function DiscordPreview({ form, isVerify, isBinding }) {
  const roles = isVerify ? [{ emoji: form.roles[0]?.emoji || "✅", label: form.roles[0]?.label || "Verify", role: form.verified_role }] : form.roles.filter((r) => r.role || r.label || r.emoji);
  return (
    <div className="force-dark bg-[#313338] rounded-lg p-4 text-[#dbdee1]" data-testid="rr-preview">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center overflow-hidden shrink-0"><BrandLogo className="w-9 h-9" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm"><span className="font-semibold text-[#fff]">crank.live</span> <span className="text-[10px] bg-[#5865F2] text-[#fff] rounded px-1 py-0.5 align-middle">BOT</span></div>
          <div className="mt-1 border-l-4 rounded-sm force-dark bg-[#2b2d31] p-3" style={{ borderColor: form.color }}>
            {form.author_name && <div className="text-xs font-semibold text-white mb-1 flex items-center gap-1">{form.author_icon && <img alt="" src={form.author_icon} className="w-5 h-5 rounded-full" />} {form.author_name}</div>}
            {form.thumbnail_url && <img alt="" src={form.thumbnail_url} className="float-right w-16 h-16 rounded ml-2 object-cover" />}
            {form.title && <div className="font-bold text-white">{form.title}</div>}
            {form.description && <div className="text-sm whitespace-pre-line mt-1">{form.description}</div>}
            {form.fields?.length > 0 && (
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2">
                {form.fields.map((f, i) => <div key={i} className={f.inline ? "" : "w-full"}><div className="text-xs font-semibold text-white">{f.name}</div><div className="text-xs">{f.value}</div></div>)}
              </div>
            )}
            {form.image_url && <img alt="" src={form.image_url} className="mt-2 rounded max-h-40 w-full object-cover" />}
            {isBinding && <div className="text-xs mt-2 text-cyan-300">🔗 Link your Discord account</div>}
            {form.footer_text && <div className="text-[11px] text-[#949ba4] mt-2 flex items-center gap-1">{form.footer_icon && <img alt="" src={form.footer_icon} className="w-4 h-4 rounded-full" />}{form.footer_text}{form.show_timestamp && " • now"}</div>}
          </div>
          {/* buttons / reactions */}
          {form.mechanism !== "reactions" && (
            <div className="flex flex-wrap gap-2 mt-2">
              {(isBinding ? [{ emoji: form.roles[0]?.emoji || (form.require_link ? "🔗" : "🎉"), label: form.roles[0]?.label || (form.require_link ? "Link Account" : "Get Role") }] : roles).map((r, i) => (
                <span key={i} className="text-sm rounded px-3 py-1.5 text-white font-medium" style={{ background: BTN_COLORS[form.button_color] || BTN_COLORS.grey }}>{r.emoji} {r.label || r.role || "Role"}</span>
              ))}
            </div>
          )}
          {form.mechanism !== "buttons" && !isBinding && (
            <div className="flex gap-2 mt-2 text-lg">{roles.map((r, i) => r.emoji && <span key={i} className="force-dark bg-[#2b2d31] rounded px-2 py-0.5">{r.emoji} <span className="text-xs text-[#949ba4]">1</span></span>)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
